import { useIntl } from "react-intl";
import { object, string, number } from "yup";
import React, { useState, useEffect } from "react";
import { Form, Button, Row, Space } from "@masterteam/mt-design-react";

import AdminFormModal from "../../../AdminFormModal";
import api from "../../../../providers/api";
import {
  addToast,
  YesNoOptions,
  UnitOfMeasureForInitiative
} from "../../../../helpers";

import "./style.scss";

const { Field } = Form;

const numberRegex = /^\d{0,19}(\.\d{1,2})?$/;
const percentageRegex = /^(\d{0,2}|100)(\.\d{1,2})?$/;

const validationSchema = object().shape({
  ArabicName: string().required(" "),
  EnglishName: string().required(" "),
  ArabicDescription: string().required(" "),
  EnglishDescription: string().required(" "),
  Value: number(),
  UnitOfMeasure: object().required(" "),
  BooleanValue: object().nullable()
});

const PerformanceIndicatorForm = ({
  data,
  closeModal,
  performanceIndicators,
  setPerformanceIndicators,
  initiativeData = null,
  getData = () => {}
}) => {
  const intl = useIntl();
  const isAr = intl.locale === "ar";

  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState({
    EnglishName: "",
    ArabicName: "",
    ArabicDescription: "",
    EnglishDescription: "",
    Value: 0,
    UnitOfMeasure: UnitOfMeasureForInitiative[0],
    BooleanValue: null
  });

  useEffect(() => {
    if (data) {
      setInitialValues({
        ...data,
        BooleanValue: YesNoOptions.find((e) => e.Value === data.BooleanValue),
        UnitOfMeasure: UnitOfMeasureForInitiative.find(
          (e) => e.value === data.UnitOfMeasure
        )
      });
    }
    setLoading(false);
  }, []);

  const onSubmit = async (values, { setSubmitting }) => {
    let body = {
      ...values,
      UnitOfMeasure: values.UnitOfMeasure.value,
      BooleanValue:
        values?.BooleanValue?.Value === null
          ? null
          : values?.BooleanValue?.Value,
      ID: data ? data.ID : performanceIndicators?.length + 1
    };

    if (initiativeData) {
      body = { ...body, InitiativeID: initiativeData.ID, ID: data?.ID };
      try {
        const service = data ? "updateKPI" : "createKPI";
        const res = await api.initiatives[service](body);
        const { StatusCode } = res;
        if (StatusCode === "Success") {
          addToast.success(data ? "toast.successUpdate" : "toast.successSave");
          getData();
          closeModal();
        }
      } catch (error) {
        addToast.error(error?.message, {
          toastId: "get-users-error"
        });
      } finally {
        setSubmitting(false);
      }
    } else if (data) {
      const newData = [...performanceIndicators];
      const index = performanceIndicators.findIndex((e) => e.ID === data.ID);
      newData[index] = body;
      setPerformanceIndicators([...newData]);
    } else setPerformanceIndicators([...performanceIndicators, body]);
    setSubmitting(false);
    closeModal();
  };

  return (
    <AdminFormModal
      title={intl.formatMessage({
        id: data
          ? "initiative.update-performance-indicator"
          : "initiative.add-performance-indicator"
      })}
      isLoading={loading}
      onClose={closeModal}
    >
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, isSubmitting, isValid, setFieldValue }) => (
          <>
            <Space
              direction="vertical"
              size="medium"
              className="performance-indicator-form-body"
            >
              <Space direction="vertical" className="container" size="large">
                <Row size="small">
                  <Field
                    name="EnglishName"
                    label={intl.formatMessage({
                      id: "initiative.english-performance-indicator-name"
                    })}
                    inputProps={{
                      maxLength: 256,
                      placeholder: intl.formatMessage({
                        id: "initiative.english-performance-indicator-name"
                      })
                    }}
                    hideErrorMessage
                    required
                  />

                  <Field
                    name="ArabicName"
                    label={intl.formatMessage({
                      id: "initiative.arabic-performance-indicator-name"
                    })}
                    inputProps={{
                      maxLength: 256,
                      placeholder: intl.formatMessage({
                        id: "initiative.arabic-performance-indicator-name"
                      })
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="small">
                  <Field
                    name="EnglishDescription"
                    label={intl.formatMessage({
                      id: "initiative.english-performance-indicator-description"
                    })}
                    inputProps={{
                      maxLength: 256,
                      placeholder: intl.formatMessage({
                        id: "initiative.english-performance-indicator-description"
                      })
                    }}
                    hideErrorMessage
                    required
                  />

                  <Field
                    name="ArabicDescription"
                    label={intl.formatMessage({
                      id: "initiative.arabic-performance-indicator-description"
                    })}
                    inputProps={{
                      maxLength: 256,
                      placeholder: intl.formatMessage({
                        id: "initiative.arabic-performance-indicator-description"
                      })
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="small">
                  <Field
                    type="select"
                    label={intl.formatMessage({
                      id: "kpi.unit-of-measure"
                    })}
                    name="UnitOfMeasure"
                    inputProps={{
                      options: UnitOfMeasureForInitiative,
                      menuPlacement: "top",
                      getOptionLabel: (el) => (isAr ? el.labelAr : el.label),
                      getOptionValue: (el) => el?.value,
                      onChange: (e) => {
                        setFieldValue("UnitOfMeasure", e);
                        setFieldValue("Value", 0);
                        setFieldValue("BooleanValue", null);
                      },
                      placeholder: intl.formatMessage({
                        id: "kpi.unit-of-measure"
                      })
                    }}
                    hideErrorMessage
                    required
                  />

                  {values.UnitOfMeasure.value !== "MultipleChoice" ? (
                    <Field
                      type="number"
                      name="Value"
                      label={intl.formatMessage({
                        id: "common.value"
                      })}
                      inputProps={{
                        onKeyDown: (evt) => {
                          if (
                            evt.key === "e" ||
                            evt.key === "-" ||
                            evt.key === "+"
                          ) {
                            evt.preventDefault();
                          }
                        },
                        onChange: (e) => {
                          if (values.UnitOfMeasure.value === "Percentage") {
                            if (percentageRegex.test(e.target.value))
                              setFieldValue("Value", e.target.value);
                          } else {
                            if (numberRegex.test(e.target.value))
                              setFieldValue("Value", e.target.value);
                          }
                        },
                        placeholder: intl.formatMessage({
                          id: "common.value"
                        })
                      }}
                      hideErrorMessage
                    />
                  ) : (
                    <Field
                      type="select"
                      label={intl.formatMessage({
                        id: "common.value"
                      })}
                      name="BooleanValue"
                      inputProps={{
                        options: YesNoOptions,
                        menuPlacement: "top",
                        getOptionLabel: (el) =>
                          isAr ? el.ArabicName : el.EnglishName,
                        getOptionValue: (el) => el?.Value,
                        placeholder: intl.formatMessage({
                          id: "common.value"
                        })
                      }}
                      hideErrorMessage
                    />
                  )}
                </Row>
              </Space>
              <Row justify="end" className="form-footer">
                <Button
                  loading={isSubmitting}
                  disabled={!isValid}
                  variant="primary"
                  type="submit"
                  fixed
                >
                  {intl.formatMessage({
                    id: data ? "common.update" : "common.add"
                  })}
                </Button>
              </Row>
            </Space>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
};

export default PerformanceIndicatorForm;
